/**
 * WYSIWYG Component Renderer
 * 
 * Renders actual component content instead of metadata shells
 */

import React, { useState, useRef } from 'react';
import { Box, Typography, Button, Card, CardContent, CardMedia } from '@mui/material';
import { Block, ComponentType } from '../../../types/page-builder.types';
import WYSIWYGRichTextEditor from '../WYSIWYGRichTextEditor';
import { convertStyleToSx } from '../../../utils/style-converter';
import ViewContentPick from '../../views/queries/ViewContentPick';
import ViewContentQuery from '../../views/queries/ViewContentQuery';
import ChatRenderer from './ChatRenderer';

interface ComponentRendererProps {
  block: Block;
  isSelected?: boolean;
  onClick?: () => void;
  readonly?: boolean;
}

// Individual Component Renderers (declared first)

const RichTextRenderer: React.FC<any> = ({ 
  html, 
  placeholder, 
  onUpdate,
  isEditing = false,
  isSelected = false,
  // Container styling properties
  backgroundColor,
  borderColor,
  borderWidth,
  borderStyle,
  borderRadius,
  padding,
  minHeight = 100
}) => {
  const [localContent, setLocalContent] = useState(html || '');
  
  // Handle content changes
  const handleChange = (value: string) => {
    setLocalContent(value);
    if (onUpdate) {
      onUpdate({ html: value });
    }
  };
  
  // If we're in edit mode and this component is selected, show the WYSIWYG editor
  if (isEditing) {
    return (
      <WYSIWYGRichTextEditor
        value={localContent}
        onChange={handleChange}
        placeholder={placeholder || 'Start typing...'}
        readonly={false}
        isSelected={isSelected}
        minHeight={minHeight}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        borderWidth={borderWidth}
        borderStyle={borderStyle}
        borderRadius={borderRadius}
        padding={padding}
      />
    );
  }
  
  // In preview/render mode, show the formatted content
  return (
    <WYSIWYGRichTextEditor
      value={localContent}
      onChange={() => {}} // No-op in readonly mode
      readonly={true}
      minHeight={minHeight}
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      borderWidth={borderWidth}
      borderStyle={borderStyle}
      borderRadius={borderRadius}
      padding={padding}
    />
  );
};

const ImageRenderer: React.FC<any> = ({
  src,
  alt,
  width,
  height,
  caption,
  alignment = 'center',
  href,
  openLinkIn = 'sameTab',
  backgroundColor,
  borderColor = '#e0e0e0',
  borderWidth = 0,
  borderStyle = 'none',
  borderRadius = 4,
  padding = 8,
  readonly = false
}) => {
  // Container styling based on properties
  const containerStyle = {
    backgroundColor: backgroundColor || 'transparent',
    border: borderStyle !== 'none' ? `${borderWidth}px ${borderStyle} ${borderColor}` : 'none',
    borderRadius: `${borderRadius}px`,
    padding: `${padding}px`,
    textAlign: alignment as 'left' | 'center' | 'right',
  };

  if (!src) {
    return (
      <Box
        sx={{
          ...containerStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          bgcolor: backgroundColor || 'grey.100',
          border: borderStyle !== 'none' ? `${borderWidth}px ${borderStyle} ${borderColor}` : '1px dashed #ccc',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          📷 Add Image URL in Properties
        </Typography>
      </Box>
    );
  }

  const imgElement = (
    <Box
      component="img"
      src={src}
      alt={alt || 'Image'}
      sx={{
        maxWidth: '100%',
        width: width ? `${width}px` : 'auto',
        height: height ? `${height}px` : 'auto',
        borderRadius: `${borderRadius}px`,
        display: alignment === 'center' ? 'block' : 'inline-block',
        marginLeft: alignment === 'center' ? 'auto' : alignment === 'right' ? 'auto' : 0,
        marginRight: alignment === 'center' ? 'auto' : alignment === 'left' ? 'auto' : 0,
      }}
    />
  );

  // Only make the image a real clickable link outside the editor — in edit mode,
  // clicks need to select the block rather than navigate away.
  const linkedImg = (readonly && href) ? (
    <Box
      component="a"
      href={href}
      target={openLinkIn === 'newTab' ? '_blank' : '_self'}
      rel={openLinkIn === 'newTab' ? 'noopener noreferrer' : undefined}
      sx={{
        display: alignment === 'center' ? 'block' : 'inline-block',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      {imgElement}
    </Box>
  ) : imgElement;

  return (
    <Box sx={containerStyle}>
      {linkedImg}
      {caption && (
        <Typography
          variant="caption"
          display="block"
          sx={{
            color: 'text.secondary',
            textAlign: alignment as 'left' | 'center' | 'right'
          }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
};

const ButtonRenderer: React.FC<any> = ({ 
  text = 'Button', 
  variant = 'contained', 
  color = 'primary', 
  size = 'medium',
  href,
  disabled = false 
}) => {
  return (
    <Button
      variant={variant as any}
      color={color as any}
      size={size as any}
      disabled={disabled}
      href={href}
      sx={{ minWidth: 100 }}
    >
      {text}
    </Button>
  );
};

const KPIRenderer: React.FC<any> = ({ 
  value = '0', 
  label = 'Metric', 
  unit, 
  trend, 
  color = 'primary' 
}) => {
  return (
    <Card sx={{ textAlign: 'center' }}>
      <CardContent>
        <Typography 
          variant="h3" 
          component="div" 
          color={color as any}
          sx={{ fontWeight: 'bold' }}
        >
          {value}{unit && <Box component="span" sx={{ fontSize: '0.6em', ml: 0.5 }}>{unit}</Box>}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {label}
        </Typography>
        {trend && (
          <Typography 
            variant="body2" 
            color={trend.startsWith('+') ? 'success.main' : trend.startsWith('-') ? 'error.main' : 'text.secondary'}
          >
            {trend}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const VideoRenderer: React.FC<any> = ({ src, poster, width, height }) => {
  if (!src) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          bgcolor: 'grey.900',
          borderRadius: 1,
          color: 'white'
        }}
      >
        <Typography variant="body2">
          🎬 Add Video URL in Properties
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'center' }}>
      <video
        controls
        poster={poster}
        style={{
          maxWidth: '100%',
          width: width ? `${width}px` : '100%',
          height: height ? `${height}px` : 'auto',
          borderRadius: 4,
        }}
      >
        <source src={src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </Box>
  );
};

const ChartRenderer: React.FC<any> = ({ 
  type = 'bar', 
  title = 'Chart Title', 
  data = [] 
}) => {
  return (
    <Card sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <Box
        sx={{
          height: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          borderRadius: 1,
          border: 1,
          borderStyle: 'dashed',
          borderColor: 'grey.300'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          📊 {type.charAt(0).toUpperCase() + type.slice(1)} Chart ({data.length} data points)
        </Typography>
      </Box>
    </Card>
  );
};

const FormRenderer: React.FC<any> = ({ 
  title = 'Form Title', 
  fields = [], 
  submitText = 'Submit' 
}) => {
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {fields.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Configure form fields in Properties panel
            </Typography>
          ) : (
            fields.map((field: any, index: number) => (
              <Box key={index}>
                <Typography variant="body2" sx={{ mb: 1 }}>{field.label || `Field ${index + 1}`}</Typography>
                <Box
                  sx={{
                    height: 40,
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    bgcolor: 'grey.50'
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {field.placeholder || `Enter ${field.label || 'value'}...`}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
          <Button variant="contained" sx={{ alignSelf: 'flex-start' }}>
            {submitText}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

const TableRenderer: React.FC<any> = ({ 
  headers = [], 
  rows = [], 
  title 
}) => {
  return (
    <Card>
      <CardContent>
        {title && <Typography variant="h6" gutterBottom>{title}</Typography>}
      <Box
        sx={{
          border: 1,
          borderColor: 'grey.300',
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        {headers.length === 0 && rows.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              📋 Configure table data in Properties panel
            </Typography>
          </Box>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            {headers.length > 0 && (
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  {headers.map((header: string, index: number) => (
                    <th key={index} style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((row: any[], rowIndex: number) => (
                <tr key={rowIndex}>
                  {row.map((cell: any, cellIndex: number) => (
                    <td key={cellIndex} style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                      {String(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Box>
      </CardContent>
    </Card>
  );
};

const DividerRenderer: React.FC<any> = ({ 
  variant = 'fullWidth',
  orientation = 'horizontal',
  thickness = 1,
  color = '#e0e0e0',
  backgroundColor,
  style = 'solid',
  marginTop = 16,
  marginBottom = 16,
  marginLeft = 0,
  marginRight = 0,
  paddingTop = 0,
  paddingBottom = 0,
  paddingLeft = 0,
  paddingRight = 0,
  borderRadius = 0,
  opacity = 1,
  width = '100%',
  height = 'auto',
  boxShadow,
  textAlign = 'center'
}) => {
  // Calculate container styles
  const containerStyle = {
    marginTop: `${marginTop}px`,
    marginBottom: `${marginBottom}px`,
    marginLeft: `${marginLeft}px`,
    marginRight: `${marginRight}px`,
    paddingTop: `${paddingTop}px`,
    paddingBottom: `${paddingBottom}px`,
    paddingLeft: `${paddingLeft}px`,
    paddingRight: `${paddingRight}px`,
    backgroundColor: backgroundColor || 'transparent',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center'
  };

  // Calculate divider width based on variant
  let dividerWidth = width;
  if (variant === 'middle') {
    dividerWidth = '60%';
  } else if (variant === 'inset') {
    dividerWidth = '80%';
  }

  // Calculate divider styles
  const dividerStyle = {
    width: orientation === 'horizontal' ? dividerWidth : `${thickness}px`,
    height: orientation === 'vertical' ? (height === 'auto' ? '100px' : height) : `${thickness}px`,
    border: 'none',
    borderTop: orientation === 'horizontal' ? `${thickness}px ${style} ${color}` : 'none',
    borderLeft: orientation === 'vertical' ? `${thickness}px ${style} ${color}` : 'none',
    backgroundColor: style === 'solid' && !backgroundColor ? color : 'transparent',
    borderRadius: borderRadius > 0 ? `${borderRadius}px` : 0,
    opacity: opacity,
    boxShadow: boxShadow || 'none',
    margin: 0,
    minHeight: orientation === 'vertical' ? (height === 'auto' ? '100px' : height) : 'auto',
    minWidth: orientation === 'horizontal' ? '20px' : 'auto'
  };

  return (
    <Box sx={containerStyle}>
      <Box
        component={orientation === 'horizontal' ? 'hr' : 'div'}
        sx={dividerStyle}
      />
    </Box>
  );
};

// Transforms common watch/share URLs into their embeddable equivalents
function transformEmbedUrl(url: string): string {
  // YouTube: watch or short URLs
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;

  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

  // Spotify
  const spotifyMatch = url.match(/open\.spotify\.com\/(track|album|playlist|artist)\/([a-zA-Z0-9]+)/);
  if (spotifyMatch) return `https://open.spotify.com/embed/${spotifyMatch[1]}/${spotifyMatch[2]}`;

  // Google Slides
  const slidesMatch = url.match(/docs\.google\.com\/presentation\/d\/([a-zA-Z0-9_-]+)/);
  if (slidesMatch) return `https://docs.google.com/presentation/d/${slidesMatch[1]}/embed`;

  // Google Docs
  const docsMatch = url.match(/docs\.google\.com\/document\/d\/([a-zA-Z0-9_-]+)/);
  if (docsMatch) return `https://docs.google.com/document/d/${docsMatch[1]}/preview`;

  // Google Sheets
  const sheetsMatch = url.match(/docs\.google\.com\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/);
  if (sheetsMatch) return `https://docs.google.com/spreadsheets/d/${sheetsMatch[1]}/preview`;

  return url;
}

// Domains known to block iframe embedding
const KNOWN_BLOCKED_DOMAINS = [
  'google.com', 'facebook.com', 'twitter.com', 'x.com', 'instagram.com',
  'linkedin.com', 'reddit.com', 'amazon.com', 'netflix.com', 'tiktok.com',
  'pinterest.com', 'snapchat.com', 'apple.com', 'microsoft.com', 'github.com',
];

function isKnownBlocked(url: string): boolean {
  try {
    const hostname = new URL(url).hostname.replace(/^www\./, '');
    return KNOWN_BLOCKED_DOMAINS.some(d => hostname === d || hostname.endsWith(`.${d}`));
  } catch {
    return false;
  }
}

const EmbedRenderer: React.FC<any> = ({
  src,
  title = 'Embedded Content',
  width,
  height = 400,
  allowFullscreen = true,
  allowScripts = true,
  allowForms = true,
  allowSameOrigin = true,
  allowPopups = true,
  allowModals = true,
  allowDownloads = true,
  loading = 'lazy'
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const allAllowed = allowScripts && allowForms && allowSameOrigin && allowPopups && allowModals && allowDownloads;
  const sandboxValue = allAllowed ? undefined : [
    allowScripts && 'allow-scripts',
    allowForms && 'allow-forms',
    allowSameOrigin && 'allow-same-origin',
    allowPopups && 'allow-popups',
    allowModals && 'allow-modals',
    allowDownloads && 'allow-downloads',
  ].filter(Boolean).join(' ') || undefined;

  if (!src) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200, border: 1, borderStyle: 'dashed', borderColor: 'grey.300', borderRadius: 1, bgcolor: 'grey.50' }}>
        <Typography variant="body2" color="text.secondary">
          🔗 Add embed URL in Properties panel
        </Typography>
      </Box>
    );
  }

  const baseUrl = src.startsWith('http://') || src.startsWith('https://') ? src : `https://${src}`;
  const embedUrl = transformEmbedUrl(baseUrl);

  return (
    <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, overflow: 'hidden', width: '100%', height: height || 400, position: 'relative', backgroundColor: 'white' }}>
      <iframe
        ref={iframeRef}
        src={embedUrl}
        width="100%"
        height={height || 400}
        style={{ border: 'none', width: '100%', height: '100%', display: 'block' }}
        title={title}
        frameBorder="0"
        allowFullScreen={allowFullscreen}
        sandbox={sandboxValue}
        loading={loading as 'lazy' | 'eager'}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      />
    </Box>
  );
};

const ViewContentPickRenderer: React.FC<any> = ({
  contentIds = [],
  displayMode = 'grid',
  bannerStyle = 'illustrations',
  maxItems = 12,
  columns = 3,
  showMetadata = true,
  showIcon = true,
  showActions = true,
  showRating = true,
  showDescription = true,
  enableContentSelector = true,
  backgroundColor,
  borderColor = '#e0e0e0',
  borderWidth = 0,
  borderStyle = 'none',
  borderRadius = 8,
  padding = 1,
  gap = 1,
  width,
  height,
  containerHeight,
  enableScroll = false,
  componentId,
  onUpdate,
  isEditing = false,
  isSelected = false
}) => {
  const handleContentIdsChange = (newContentIds: string[]) => {
    if (onUpdate) {
      onUpdate({ contentIds: newContentIds });
    }
  };

  return (
    <ViewContentPick
      contentIds={contentIds}
      displayMode={displayMode}
      bannerStyle={bannerStyle}
      maxItems={maxItems}
      columns={columns}
      showMetadata={showMetadata}
      showIcon={showIcon}
      showActions={showActions}
      showRating={showRating}
      showDescription={showDescription}
      enableContentSelector={enableContentSelector}
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      isEditing={isEditing}
      borderWidth={borderWidth}
      borderStyle={borderStyle}
      borderRadius={borderRadius}
      padding={padding}
      gap={gap}
      width={width}
      height={height}
      containerHeight={containerHeight}
      enableScroll={enableScroll}
      componentId={componentId}
      onContentIdsChange={handleContentIdsChange}
    />
  );
};

const ViewContentQueryRenderer: React.FC<any> = ({
  searchQuery = '',
  filters = {},
  sortBy = 'updatedAt',
  sortOrder = 'desc',
  displayMode = 'grid',
  bannerStyle = 'illustrations',
  maxItems = 12,
  columns = 3,
  showMetadata = true,
  showIcon = true,
  showActions = true,
  showRating = true,
  showDescription = true,
  enableQueryBuilder = true,
  pageSize,
  backgroundColor,
  borderColor = '#e0e0e0',
  borderWidth = 0,
  borderStyle = 'none',
  borderRadius = 8,
  padding = 1,
  gap = 1,
  width,
  height,
  containerHeight,
  enableScroll = false,
  autoRefresh = false,
  refreshInterval = 60,
  componentId,
  onUpdate,
  isEditing = false,
  isSelected = false
}) => {
  const handleQueryChange = (query: string, filters: any, sortBy: string, sortOrder: string) => {
    if (onUpdate) {
      onUpdate({
        searchQuery: query,
        filters,
        sortBy,
        sortOrder
      });
    }
  };

  return (
    <ViewContentQuery
      searchQuery={searchQuery}
      filters={filters}
      sortBy={sortBy}
      sortOrder={sortOrder}
      displayMode={displayMode}
      bannerStyle={bannerStyle}
      maxItems={maxItems}
      columns={columns}
      showMetadata={showMetadata}
      showIcon={showIcon}
      showActions={showActions}
      showRating={showRating}
      showDescription={showDescription}
      enableQueryBuilder={enableQueryBuilder}
      pageSize={pageSize}
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      isEditing={isEditing}
      borderWidth={borderWidth}
      borderStyle={borderStyle}
      borderRadius={borderRadius}
      padding={padding}
      gap={gap}
      width={width}
      height={height}
      containerHeight={containerHeight}
      enableScroll={enableScroll}
      autoRefresh={autoRefresh}
      refreshInterval={refreshInterval}
      componentId={componentId}
      onQueryChange={handleQueryChange}
    />
  );
};

const UnknownComponentRenderer: React.FC<{ type: string; props: any }> = ({ type, props }) => {
  return (
    <Box
      sx={{
        p: 2,
        border: 1,
        borderStyle: 'dashed',
        borderColor: 'error.main',
        borderRadius: 1,
        bgcolor: 'error.light',
        textAlign: 'center'
      }}
    >
      <Typography variant="body2" color="error.main">
        Unknown Component: {type}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {Object.keys(props).length} properties
      </Typography>
    </Box>
  );
};

// Main Component Renderer
const ComponentRenderer: React.FC<ComponentRendererProps & { onUpdate?: (props: any) => void }> = ({
  block,
  isSelected = false,
  onClick,
  readonly = false,
  onUpdate
}) => {
  const renderContent = () => {
    switch (block.type) {
      case 'RichText':
        return <RichTextRenderer 
          {...block.props} 
          isEditing={!readonly}
          isSelected={isSelected}
          onUpdate={onUpdate}
        />;
      
      case 'Image':
        return <ImageRenderer {...block.props} readonly={readonly} />;
      
      case 'Divider':
        return <DividerRenderer {...block.props} />;
      
      // Hidden components - commented out to remove from component palette
      // case 'Button':
      //   return <ButtonRenderer {...block.props} />;
      // 
      // case 'KPI':
      //   return <KPIRenderer {...block.props} />;
      // 
      // case 'Video':
      //   return <VideoRenderer {...block.props} />;
      // 
      // case 'Chart':
      //   return <ChartRenderer {...block.props} />;
      // 
      // case 'Form':
      //   return <FormRenderer {...block.props} />;
      // 
      // case 'Table':
      //   return <TableRenderer {...block.props} />;
      
      case 'Embed':
        return <EmbedRenderer {...block.props} />;
      
      case 'ViewContentPick':
        return <ViewContentPickRenderer
          {...block.props}
          componentId={block.id}
          isEditing={!readonly}
          isSelected={isSelected}
          onUpdate={onUpdate}
        />;

      case 'ViewContentQuery':
        return <ViewContentQueryRenderer
          {...block.props}
          componentId={block.id}
          isEditing={!readonly}
          isSelected={isSelected}
          onUpdate={onUpdate}
        />;

      case 'Chat':
        return (
          <ChatRenderer
            {...block.props}
            componentId={block.id}
            isEditing={!readonly}
            isSelected={isSelected}
            onUpdate={onUpdate}
          />
        );

      default:
        return <UnknownComponentRenderer type={block.type} props={block.props} />;
    }
  };

  // Convert block style to sx props
  const blockSx = convertStyleToSx(block.style);

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: onClick && !readonly ? 'pointer' : 'default',
        '&:hover': onClick && !readonly ? {
          outline: '1px solid',
          outlineColor: 'action.focus',
          outlineOffset: 2,
        } : {},
        ...blockSx  // Apply block styles
      }}
    >
      {renderContent()}
    </Box>
  );
};

export default ComponentRenderer;